import { EXPO_BOARD_STATUSES, SERVE_DEFAULTS } from '@didto/domain';
import type { Row } from './db';
import type { ItemLine } from './kds';
import { supabase } from './supabase';

/**
 * Reads for the Expo / pass board (Phase 6, ADR-021). One round trip per list: orders with their
 * production tickets and the items behind each ticket. Writes go through runCommand with an
 * explicit retry (app/directActions.ts), never through the outbox.
 */
export type ExpoItem = Pick<Row<'production_item'>, 'id' | 'status' | 'qty'> & {
  order_item: ItemLine | null;
};
export type ExpoTicket = Pick<
  Row<'production_ticket'>,
  | 'id'
  | 'order_id'
  | 'production_station_id'
  | 'ticket_seq'
  | 'is_addition'
  | 'status'
  | 'sent_at'
  | 'ready_at'
  | 'served_at'
  | 'first_served_at'
  | 'served_by'
  | 'version'
> & { production_item: ExpoItem[] };
export type ExpoOrder = Pick<
  Row<'orders'>,
  | 'id'
  | 'order_number'
  | 'type'
  | 'status'
  | 'payment_status'
  | 'fulfilment_status'
  | 'dining_table_id'
  | 'covers'
  | 'customer_name'
  | 'scheduled_for'
  | 'notes'
  | 'placed_at'
  | 'sent_at'
  | 'ready_at'
  | 'served_at'
  | 'first_served_at'
  | 'total_minor'
  | 'paid_minor'
  | 'refunded_minor'
  | 'written_off_minor'
  | 'version'
  | 'table_session_id'
> & { production_ticket: ExpoTicket[] };

/** A recently served ticket with its order header, for the undo and correction drawer. */
export type ServedTicket = ExpoTicket & {
  orders: Pick<
    Row<'orders'>,
    | 'order_number'
    | 'type'
    | 'status'
    | 'payment_status'
    | 'fulfilment_status'
    | 'dining_table_id'
    | 'customer_name'
  > | null;
};

export const expoKeys = {
  all: ['expo'] as const,
  board: (locationId: string) => ['expo', 'board', locationId] as const,
  settle: (locationId: string) => ['expo', 'settle', locationId] as const,
  recent: (locationId: string) => ['expo', 'recent', locationId] as const,
};

const TICKET_SELECT = `id, order_id, production_station_id, ticket_seq, is_addition, status, sent_at, ready_at, served_at, first_served_at, served_by, version,
  production_item(id, status, qty, order_item(name_snapshot, variant_name_snapshot, notes, status, seq, order_item_modifier(option_name_snapshot)))`;
const ORDER_SELECT = `id, order_number, type, status, payment_status, fulfilment_status, dining_table_id, covers, customer_name, scheduled_for, notes,
  placed_at, sent_at, ready_at, served_at, first_served_at, total_minor, paid_minor, refunded_minor, written_off_minor, version, table_session_id,
  production_ticket(${TICKET_SELECT})`;

async function rows<T>(q: PromiseLike<{ data: unknown; error: unknown }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as T[];
}

/** Open orders with at least one ready or served station (owner decision 2), oldest placed first. */
export const fetchExpoBoard = (locationId: string) =>
  rows<ExpoOrder>(
    supabase
      .from('orders')
      .select(ORDER_SELECT)
      .eq('location_id', locationId)
      .eq('status', 'open')
      .in('fulfilment_status', [...EXPO_BOARD_STATUSES])
      .order('placed_at'),
  );

/**
 * Served but still open and not settled: the orders the counter has to settle (decision 4). Same rule
 * as `isToSettle`; a paid or written-off (ADR-031) served order completes, so it never stays here.
 */
export const fetchToSettle = (locationId: string) =>
  rows<ExpoOrder>(
    supabase
      .from('orders')
      .select(ORDER_SELECT)
      .eq('location_id', locationId)
      .eq('status', 'open')
      .eq('fulfilment_status', 'served')
      .not('payment_status', 'in', '(paid,written_off)')
      .order('served_at', { ascending: false }),
  );

/** Bounded recently served drawer: the last serves inside the window, newest first. */
export const fetchRecentlyServed = (locationId: string) =>
  rows<ServedTicket>(
    supabase
      .from('production_ticket')
      .select(
        `${TICKET_SELECT}, orders(order_number, type, status, payment_status, fulfilment_status, dining_table_id, customer_name)`,
      )
      .eq('location_id', locationId)
      .eq('status', 'served')
      .gte(
        'served_at',
        new Date(Date.now() - SERVE_DEFAULTS.recentWindowMinutes * 60_000).toISOString(),
      )
      .order('served_at', { ascending: false })
      .limit(SERVE_DEFAULTS.recentLimit),
  );

/** Tickets of an order in pass order: ticket sequence, then id. */
export function orderTickets(o: { production_ticket: ExpoTicket[] }): ExpoTicket[] {
  return [...o.production_ticket].sort(
    (a, b) => a.ticket_seq - b.ticket_seq || a.id.localeCompare(b.id),
  );
}

/** Items of a ticket in order-line order. */
export function expoItems(t: ExpoTicket): ExpoItem[] {
  return [...t.production_item].sort(
    (a, b) => (a.order_item?.seq ?? 0) - (b.order_item?.seq ?? 0) || a.id.localeCompare(b.id),
  );
}
